import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import Reveal from "../Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Hospitality & Tourism",
  "Real Estate & Infrastructure",
  "Trade & Manufacturing",
  "Technology & BPO",
  "Lifestyle / Melissa's Brand",
  "General Inquiry",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    interest: INTERESTS[0],
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({
        state: "success",
        message:
          "Thank you. Your inquiry has reached our office — we will respond within two business days.",
      });
      setForm({ name: "", email: "", company: "", interest: INTERESTS[0], message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "We couldn't submit your inquiry. Please try again.";
      setStatus({ state: "error", message: typeof msg === "string" ? msg : "Submission failed." });
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-white py-28 lg:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <p className="overline">Engage Us</p>
          <h2
            data-testid="contact-heading"
            className="font-serif font-medium text-4xl lg:text-6xl mt-6 leading-[1.02] tracking-tight"
          >
            Let's build the
            <br />
            <span className="italic text-gold">next decade</span> together.
          </h2>

          <p className="mt-8 text-base text-[--kdm-ink-soft] max-w-md leading-relaxed">
            Whether you're exploring an investment partnership, a hotel residency at
            Melissa's Riviera or a sourcing opportunity from our Dhaka facility — our
            international desks are open.
          </p>

          <div className="mt-12 space-y-6 border-t border-[--kdm-line] pt-10">
            <div>
              <p className="overline">Group HQ</p>
              <p className="font-serif text-xl mt-2">Yakkala, Sri Lanka</p>
            </div>
            <div>
              <p className="overline">European Desk</p>
              <p className="font-serif text-xl mt-2">Milan · Girona</p>
            </div>
            <div>
              <p className="overline">North America</p>
              <p className="font-serif text-xl mt-2">United States</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <Reveal className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="card-soft p-8 lg:p-12 space-y-2"
          >
            <div className="grid sm:grid-cols-2 gap-x-8">
              <div>
                <label className="overline">Full Name</label>
                <input
                  required
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  className="kdm-input"
                  placeholder="Your name"
                  data-testid="contact-input-name"
                />
              </div>
              <div>
                <label className="overline">Email</label>
                <input
                  required
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  className="kdm-input"
                  placeholder="you@company.com"
                  data-testid="contact-input-email"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-x-8 mt-2">
              <div>
                <label className="overline">Company / Organisation</label>
                <input
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  className="kdm-input"
                  placeholder="Optional"
                  data-testid="contact-input-company"
                />
              </div>
              <div>
                <label className="overline">Area of Interest</label>
                <select
                  name="interest"
                  value={form.interest}
                  onChange={onChange}
                  className="kdm-input bg-transparent"
                  data-testid="contact-input-interest"
                >
                  {INTERESTS.map((i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-2">
              <label className="overline">Message</label>
              <textarea
                required
                name="message"
                rows={5}
                value={form.message}
                onChange={onChange}
                className="kdm-input resize-none"
                placeholder="Tell us about your project, partnership or inquiry…"
                data-testid="contact-input-message"
              />
            </div>

            <div className="pt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <p className="text-xs text-[--kdm-ink-soft] max-w-sm leading-relaxed">
                By submitting you agree to be contacted by KDM Group regarding your inquiry.
              </p>
              <button
                type="submit"
                disabled={status.state === "loading"}
                data-testid="contact-submit"
                className="btn-gold disabled:opacity-50"
              >
                {status.state === "loading" ? "Sending…" : "Send Inquiry"}
                <ArrowRight size={14} />
              </button>
            </div>

            {status.state === "success" && (
              <div
                data-testid="contact-success"
                className="mt-6 flex items-start gap-3 text-sm text-[--kdm-ink] bg-[--kdm-gold-soft]/40 border border-[--kdm-gold] p-4"
              >
                <CheckCircle2 size={18} className="text-gold mt-0.5 shrink-0" />
                <span>{status.message}</span>
              </div>
            )}
            {status.state === "error" && (
              <div
                data-testid="contact-error"
                className="mt-6 flex items-start gap-3 text-sm text-red-700 bg-red-50 border border-red-200 p-4"
              >
                <AlertCircle size={18} className="mt-0.5 shrink-0" />
                <span>{status.message}</span>
              </div>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
}
